// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import { Button, Col, Form, Input, Popover, Row, Select, Steps } from "antd";
import * as AuthBackend from "./AuthBackend";
import * as ApplicationBackend from "../backend/ApplicationBackend";
import * as Util from "./Util";
import * as Setting from "../Setting";
import i18next from "i18next";
// import { SendCodeInput } from "../common/SendCodeInput";
import { SendResetLink } from "../common/SendResetLink";
import * as UserBackend from "../backend/UserBackend";
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  KeyOutlined,
  LockOutlined,
  SolutionOutlined,
  UserOutlined,
} from "@ant-design/icons";
import CustomGithubCorner from "../common/CustomGithubCorner";
import LanguageSelect from "../common/select/LanguageSelect";
import { withRouter } from "react-router-dom";
import * as PasswordChecker from "../common/PasswordChecker";
import * as Obfuscator from "./Obfuscator";

const { Option } = Select;

class ForgetPage extends React.Component {
  constructor(props) {
    super(props);
    const queryParams = new URLSearchParams(location.search);
    this.state = {
      classes: props,
      applicationName:
        props.applicationName ?? props.match.params?.applicationName,
      msg: null,
      name: props.account ? props.account.name : queryParams.get("username"),
      username: props.account ? props.account.name : "",
      phone: "",
      email: "",
      dest: "",
      isVerifyTypeFixed: false,
      verifyType: "", // "email", "phone"
      current: queryParams.get("code") ? 2 : 0,
      code: queryParams.get("code"),
      queryParams: queryParams,
      user: {},
      hasCode: queryParams.get("code") ? true : false,
      isChangePasswordDisabled: true,
      isChangingPassword: false,
      linkSent: false,
    };
    this.form = React.createRef();
  }

  async componentDidMount() {
    if (this.getApplicationObj() === undefined) {
      if (this.state.applicationName !== undefined) {
        this.getApplication();
      } else {
        Setting.showMessage(
          "error",
          i18next.t("forget:Unknown forget type") + ": " + this.state.type
        );
      }
    }

    // When entering step 2 directly via reset link, check if the link is valid and fetch user data
    const application = this.getApplicationObj();
    if (this.state.hasCode && this.state.name) {
      this.checkResetLinkValidity();
      if (application) {
        this.fetchUserData(application.organization);
      }
    }
  }

  fetchUserData(organization) {
    if (!organization || !this.state.name) {
      return;
    }

    AuthBackend.getEmailAndPhone(organization, this.state.name).then((res) => {
      if (res.status === "ok") {
        this.setState({
          user: res.data,
        });
      }
    });
  }

  checkResetLinkValidity() {
    const application = this.getApplicationObj();
    const applicationName = this.state.applicationName;
    const appOwner = application?.owner || "admin";
    const applicationId = `${appOwner}/${applicationName}`;
    const username = this.state.name;
    const code = this.state.code;

    if (!username || !code) {
      return;
    }

    UserBackend.checkResetLink(applicationId, username, code).then((res) => {
      if (res.status === "ok" && res.data?.valid === true) {
        // link is valid, do nothing
      } else {
        const message = res.data?.message || res.msg || "";
        Setting.showMessage("error", message);
        this.setState({ current: 0, hasCode: false, code: "" });
      }
    }).catch(() => {
      this.setState({ current: 0, hasCode: false, code: "" });
    });
  }

  getApplication() {
    if (this.state.applicationName === undefined) {
      return;
    }

    ApplicationBackend.getApplication("admin", this.state.applicationName).then(
      (res) => {
        if (res.status === "error") {
          Setting.showMessage("error", res.msg);
          return;
        }
        this.onUpdateApplication(res.data);

        // After application loads, fetch user data if entering step 2 directly via reset link
        if (this.state.hasCode && this.state.name && res.data) {
          this.fetchUserData(res.data.organization);
        }
      }
    );
  }
  getApplicationObj() {
    return this.props.application;
  }

  onUpdateApplication(application) {
    this.props.onUpdateApplication(application);
  }

  onFormFinish(name, info, forms) {
    switch (name) {
      case "step1":
        // Modify: username trim
        const username = forms.step1.getFieldValue("username").trim();
        AuthBackend.getEmailAndPhone(
          forms.step1.getFieldValue("organization"),
          username
        ).then((res) => {
          if (res.status === "ok") {
            // 查询成功：写入 wmUserInfo.userId，供前端监控脚本关联用户
            const wmUserInfo = window.localStorage.wmUserInfo
              ? JSON.parse(window.localStorage.wmUserInfo)
              : {};
            window.localStorage.wmUserInfo = JSON.stringify({
              ...wmUserInfo,
              userId: res.data.name,
            });
            const phone = res.data.phone;
            const email = res.data.email;

            if (!phone && !email) {
              Setting.showMessage(
                "error",
                i18next.t("general:No verification method")
              );
            } else {
              this.setState({
                name: res.data.name,
                phone: phone,
                email: email,
                user: res.data,
              });
              const saveFields = (type, dest, fixed) => {
                this.setState({
                  verifyType: type,
                  isVerifyTypeFixed: fixed,
                  dest: dest,
                });
              };

              switch (res.data2) {
                case "email":
                  saveFields("email", email, true);
                  break;
                case "phone":
                  saveFields("phone", phone, true);
                  break;
                case "username":
                  phone !== ""
                    ? saveFields("phone", phone, false)
                    : saveFields("email", email, false);
              }

              this.setState({
                current: 1,
              });
            }
          } else {
            // 查询失败：清空 wmUserInfo.userId，避免沿用上次成功查询的用户标识
            const wmUserInfo = window.localStorage.wmUserInfo
              ? JSON.parse(window.localStorage.wmUserInfo)
              : {};
            delete wmUserInfo.userId;
            window.localStorage.wmUserInfo = JSON.stringify(wmUserInfo);
            Setting.showMessage("error", res.msg);
          }
        });
        break;
      case "step2":
        // UserBackend.verifyCode({
        //   application: forms.step2.getFieldValue('application'),
        //   organization: forms.step2.getFieldValue('organization'),
        //   username: forms.step2.getFieldValue('dest'),
        //   name: this.state.name,
        //   code: forms.step2.getFieldValue('code'),
        //   type: 'login'
        // }).then(res => {
        //   if (res.status === 'ok') {
        //     this.setState({
        //       current: 2,
        //       code: forms.step2.getFieldValue('code')
        //     })
        //   } else {
        //     Setting.showMessage('error', res.msg)
        //   }
        // })

        break;
      default:
        break;
    }
  }

  async onFinish(values) {
    if (this.state.isChangingPassword) {
      return;
    }

    this.setState({isChangingPassword: true});
    let passwordChanged = false;
    try {
      passwordChanged = await this.submitPassword(values);
    } catch (error) {
      Setting.showMessage("error", error.message);
    } finally {
      if (!passwordChanged) {
        this.setState({isChangingPassword: false});
      }
    }
  }

  async submitPassword(values) {
    values.username = this.state.name;
    values.userOwner = this.getApplicationObj()?.organizationObj.name;

    if (this.state.queryParams.get("code")) {
      const res = await UserBackend.verifyCode({
        application: this.getApplicationObj().name,
        organization: values.userOwner,
        username: this.state.queryParams.get("dest"),
        name: this.state.name,
        code: this.state.code,
        type: "login",
      });

      if (res.status !== "ok") {
        Setting.showMessage("error", res.msg);
        return;
      }
    }

    // Encrypt password using password obfuscator if configured
    let encryptedNewPassword = values?.newPassword;
    const organization = this.getApplicationObj()?.organizationObj;

    if (
      organization?.passwordObfuscatorType &&
      organization.passwordObfuscatorType !== "Plain"
    ) {
      const [passwordCipher, errorMessage] =
        Obfuscator.encryptByPasswordObfuscator(
          organization.passwordObfuscatorType,
          organization.passwordObfuscatorKey,
          values?.newPassword
        );
      if (errorMessage.length > 0) {
        Setting.showMessage("error", errorMessage);
        return;
      }
      encryptedNewPassword = passwordCipher;
    }

    return UserBackend.setPassword(
      values.userOwner,
      values.username,
      "",
      encryptedNewPassword,
      this.state.code
    ).then((res) => {
      if (res.status === "ok") {
        const linkInStorage = sessionStorage.getItem("signinUrl");
        if (linkInStorage !== null && linkInStorage !== "") {
          Setting.goToLinkSoft(this, linkInStorage);
        } else {
          Setting.redirectToLoginPage(
            this.getApplicationObj(),
            this.props.history
          );
        }
        return true;
      } else {
        Setting.showMessage("error", res.msg);
      }
    });
  }

  onFinishFailed(values, errorFields) {}

  renderOptions() {
    const options = [];

    if (this.state.phone !== "") {
      options.push(
        <Option key={"phone"} value={this.state.phone}>
          &nbsp;&nbsp;{this.state.phone}
        </Option>
      );
    }

    if (this.state.email !== "") {
      options.push(
        <Option key={"email"} value={this.state.email}>
          &nbsp;&nbsp;{this.state.email}
        </Option>
      );
    }

    return options;
  }

  renderForm(application) {
    return (
      <Form.Provider
        onFormFinish={(name, { info, forms }) => {
          this.onFormFinish(name, info, forms);
        }}
      >
        {/* STEP 1: input username -> get email & phone */}
        {this.state.current === 0 ? (
          <Form
            ref={this.form}
            name="step1"
            // eslint-disable-next-line no-console
            onFinishFailed={(errorInfo) => console.log(errorInfo)}
            initialValues={{
              application: application.name,
              organization: application.organization,
              // username: this.state.name
              username: this.state.email,
            }}
            style={{ width: "390px" }}
            size="large"
          >
            <Form.Item
              hidden
              name="application"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "application:Please input your application!"
                  ),
                },
              ]}
            />
            <Form.Item
              hidden
              name="organization"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "application:Please input your organization!"
                  ),
                },
              ]}
            />
            <Form.Item
              name="username"
              rules={[
                {
                  required: true,
                  message: i18next.t("forget:Please input your username!"),
                  whitespace: true,
                },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                // placeholder={i18next.t('login:username, Email or phone')}
                // Modify: default placeholder
                placeholder={i18next.t("general:Email")}
              />
            </Form.Item>
            <div style={{ height: "10px" }}></div>
            <Form.Item>
              <Button block type="primary" htmlType="submit">
                {i18next.t("forget:Next Step")}
              </Button>
            </Form.Item>
          </Form>
        ) : null}

        {/* STEP 2: verify email or phone */}
        {this.state.current === 1 ? (
          <Form
            ref={this.form}
            name="step2"
            onFinishFailed={(errorInfo) =>
              this.onFinishFailed(
                errorInfo.values,
                errorInfo.errorFields,
                errorInfo.outOfDate
              )
            }
            onValuesChange={(changedValues, allValues) => {
              if (!changedValues.dest) {
                return;
              }
              const verifyType =
                changedValues.dest?.indexOf("@") === -1 ? "phone" : "email";
              this.setState({
                dest: changedValues.dest,
                verifyType: verifyType,
              });
            }}
            initialValues={{
              application: application.name,
              organization: application.organization,
              dest: this.state.dest,
            }}
            style={{ width: "390px" }}
            size="large"
          >
            <Form.Item
              style={{ height: 0, visibility: "hidden" }}
              name="application"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "application:Please input your application!"
                  ),
                },
              ]}
            />
            <Form.Item
              hidden
              name="organization"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "application:Please input your organization!"
                  ),
                },
              ]}
            />
            {this.state.linkSent && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 16, textAlign: "left", fontWeight: "bold" }}>
                <CheckCircleFilled style={{ fontSize: "14px", color: "#52c41a", flexShrink: 0, marginTop: 3 }} />
                <div>
                  {i18next.t("forget:Reset link sent").split(/(?<=\.)\s+/).map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ position: "relative" }}>
              <Form.Item name="dest" validateFirst hasFeedback>
                {
                  <Select
                    virtual={false}
                    disabled={this.state.isVerifyTypeFixed}
                    style={{ textAlign: "left" }}
                    placeholder={i18next.t("forget:Choose email or phone")}
                  >
                    {this.renderOptions()}
                  </Select>
                }
              </Form.Item>
              {!this.state.linkSent && (
                <CheckCircleOutlined style={{ fontSize: "20px", color: "#52c41a", position: "absolute", right: -28, top: 6 }} />
              )}
            </div>
            {/* <Form.Item
              name='code'
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    'code:Please input your verification code!'
                  )
                }
              ]}>
              <SendCodeInput
                disabled={this.state.dest === ''}
                method={'forget'}
                onButtonClickArgs={[
                  this.state.dest,
                  this.state.verifyType,
                  Setting.getApplicationName(this.getApplicationObj()),
                  this.state.name
                ]}
                application={application}
              />
            </Form.Item> */}
            {/* <br /> */}
            <Form.Item>
              {/* <Button block type='primary' htmlType='submit'>
                {i18next.t('forget:Next Step')}
                {i18next.t('forget:Send Reset Link')}
              </Button> */}
              <SendResetLink
                method="forget"
                username={this.state.name}
                application={application}
                onSent={() => this.setState({ linkSent: true })}
                onTimerEnd={() => this.setState({ linkSent: false })}
              />
            </Form.Item>
          </Form>
        ) : null}

        {/* STEP 3 */}
        {this.state.current === 2 ? (
          <Form
            ref={this.form}
            name="step3"
            onFinish={(values) => this.onFinish(values)}
            onFinishFailed={(errorInfo) =>
              this.onFinishFailed(
                errorInfo.values,
                errorInfo.errorFields,
                errorInfo.outOfDate
              )
            }
            onFieldsChange={() => {
              setTimeout(() => {
                if (this.form.current) {
                  const values = this.form.current.getFieldsValue(["newPassword", "confirm"]);
                  const hasValues = values.newPassword && values.confirm;
                  if (!hasValues) {
                    this.setState({isChangePasswordDisabled: true});
                    return;
                  }

                  // Validate newPassword (complexity) and confirm (match) separately
                  // to avoid confirm validator seeing stale newPassword value
                  const passwordError = PasswordChecker.checkPasswordComplexity(
                    values.newPassword,
                    application.organizationObj.passwordOptions,
                    this.state.user
                  );
                  const isMatch = values.newPassword === values.confirm;

                  if (passwordError === "" && isMatch) {
                    this.form.current.setFields([{ name: "confirm", errors: [] }]);
                    this.setState({isChangePasswordDisabled: false});
                  } else {
                    if (!isMatch) {
                      this.form.current.setFields([{
                        name: "confirm",
                        errors: [i18next.t("modification:The passwords you entered don't match")],
                      }]);
                    } else {
                      this.form.current.setFields([{ name: "confirm", errors: [] }]);
                    }
                    this.setState({isChangePasswordDisabled: true});
                  }
                }
              }, 0);
            }}
            initialValues={{
              application: application.name,
              organization: application.organization,
            }}
            style={{ width: "390px" }}
            size="large"
          >
            <Form.Item
              hidden
              name="application"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "application:Please input your application!"
                  ),
                },
              ]}
            />
            <Form.Item
              hidden
              name="organization"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "application:Please input your organization!"
                  ),
                },
              ]}
            />
            <Popover
              placement={window.innerWidth >= 960 ? "right" : "top"}
              content={this.state.passwordPopover}
              open={this.state.passwordPopoverOpen}
            >
              <Form.Item
                name="newPassword"
                hidden={this.state.current !== 2}
                rules={[
                  {
                    required: true,
                    validateTrigger: "onChange",
                    validator: (rule, value) => {
                      const errorMsg = PasswordChecker.checkPasswordComplexity(
                        value,
                        application.organizationObj.passwordOptions,
                        this.state.user
                      );
                      if (errorMsg === "") {
                        return Promise.resolve();
                      } else {
                        return Promise.reject();
                        // return Promise.reject(errorMsg)
                      }
                    },
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder={i18next.t("general:Password")}
                  onChange={(e) => {
                    this.setState({
                      passwordPopover: PasswordChecker.renderPasswordPopover(
                        application.organizationObj.passwordOptions,
                        e.target.value,
                        this.state.user
                      ),
                    });
                  }}
                  onFocus={() => {
                    this.setState({
                      passwordPopoverOpen:
                        application.organizationObj.passwordOptions?.length > 0,
                      passwordPopover: PasswordChecker.renderPasswordPopover(
                        application.organizationObj.passwordOptions,
                        this.form.current?.getFieldValue("newPassword") ?? "",
                        this.state.user
                      ),
                    });
                  }}
                  onBlur={() => {
                    this.setState({
                      passwordPopoverOpen: false,
                    });
                  }}
                />
              </Form.Item>
            </Popover>
            <Form.Item
              name="confirm"
              dependencies={["newPassword"]}
              hasFeedback
              rules={[
                {
                  required: true,
                  message: i18next.t("signup:Please confirm your password!"),
                },
                ({ getFieldValue }) => ({
                  validator(rule, value) {
                    if (!value || getFieldValue("newPassword") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      i18next.t(
                        "modification:The passwords you entered don’t match"
                      )
                    );
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<CheckCircleOutlined />}
                placeholder={i18next.t("signup:Confirm")}
              />
            </Form.Item>
            <div style={{ height: "10px" }}></div>
            <Form.Item hidden={this.state.current !== 2}>
              <Button block type="primary" htmlType="submit" loading={this.state.isChangingPassword} disabled={this.state.isChangePasswordDisabled}>
                {i18next.t("forget:Change Password")}
              </Button>
            </Form.Item>
          </Form>
        ) : null}
      </Form.Provider>
    );
  }

  stepBack() {
    if (this.state.current > 0) {
      this.setState({
        current: this.state.current - 1,
      });
    } else if (this.props.history.length > 1) {
      this.props.history.goBack();
    } else {
      Setting.redirectToLoginPage(this.getApplicationObj(), this.props.history);
    }
  }

  renderApplicationInfo() {
    const application = this.getApplicationObj();
    return application.name ? (
      <div className="loginApplication">
        <div className="application-name">e-Channel</div>
        <div className="application-description">
          Online Invoicing and Payment Portal
        </div>
      </div>
    ) : null;
  }

  render() {
    const application = this.getApplicationObj();
    if (application === undefined) {
      return null;
    }
    if (application === null) {
      return Util.renderMessageLarge(this, this.state.msg);
    }

    return (
      <React.Fragment>
        <CustomGithubCorner />
        <div
          className="forget-content"
          style={{
            position: "relative",
            width: "700px",
            padding:
              Setting.isMobile() || application.name === "e-channel"
                ? "0"
                : null,
            boxShadow: Setting.isMobile() ? "none" : null,
            margin: application.name ? "30px 10% 30px auto" : "30px auto",
            minHeight: "580px",
          }}
        >
          {Setting.inIframe() || Setting.isMobile() ? null : (
            <div dangerouslySetInnerHTML={{ __html: application.formCss }} />
          )}
          {Setting.inIframe() || !Setting.isMobile() ? null : (
            <div
              dangerouslySetInnerHTML={{ __html: application.formCssMobile }}
            />
          )}
          <div
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              zIndex: 1000,
            }}
          >
            <LanguageSelect
              languages={application.organizationObj?.languages}
            />
          </div>
          {!this.state.hasCode ? (
            <Button
              type="text"
              style={{
                position: "relative",
                left: Setting.isMobile() ? "10px" : "0",
                top: 0,
              }}
              icon={<ArrowLeftOutlined style={{ fontSize: "24px" }} />}
              size={"large"}
              onClick={() => {
                this.stepBack();
              }}
            >
              <div>{i18next.t("general:Back")}</div>
            </Button>
          ) : null}
          <Row style={{ minHeight: "580px" }}>
            <Col span={24} style={{ justifyContent: "center" }}>
              <Row>
                <Col span={24}>
                  <div
                    style={{
                      marginTop: "80px",
                      marginBottom: "10px",
                      textAlign: "center",
                    }}
                  >
                    {Setting.renderHelmet(application)}
                    {Setting.renderLogo(application)}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <div style={{ textAlign: "center", fontSize: "28px" }}>
                    {i18next.t("forget:Reset password")}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  {/* <Steps
                    current={this.state.current}
                    items={[
                      {
                        title: i18next.t('forget:Account'),
                        icon: <UserOutlined />
                      },
                      {
                        title: i18next.t('forget:Verify'),
                        icon: <SolutionOutlined />
                      },
                      {
                        title: i18next.t('forget:Reset'),
                        icon: <KeyOutlined />
                      }
                    ]}
                    style={{
                      width: '90%',
                      maxWidth: '500px',
                      margin: 'auto',
                      marginTop: '80px'
                    }}></Steps> */}
                  {this.state.current === 0 && (
                    <div style={{ textAlign: "center", marginTop: "60px" }}>
                      {i18next.t("forget:Reset link prompt").split(/(?<=[。.])\s+/).map((line, index) => (
                        <div key={index}>{line}</div>
                      ))}
                    </div>
                  )}
                </Col>
              </Row>
            </Col>
            <Col
              span={24}
              style={{ display: "flex", justifyContent: "center" }}
            >
              <div style={{ marginTop: "40px", textAlign: "center" }}>
                {this.renderForm(application)}
              </div>
            </Col>
          </Row>
        </div>
        {this.renderApplicationInfo()}
      </React.Fragment>
    );
  }
}

export default withRouter(ForgetPage);
